import { DEFAULT_FONT } from './themeStorage';

/**
 * User Data Loader
 * 
 * Loads all UserData configurations with priority:
 * 1. Bundled config (compile-time, fastest, no network request)
 * 2. Runtime fetch from /UserData/ (if bundled not available)
 * 3. Fallback defaults (if both fail)
 * 
 * This eliminates flash of default content while maintaining demo mode functionality
 * (localStorage overrides bundled configs in App.js/Dashboard.js).
 */

// Import bundled configs (generated by prebuild script)
import { bundledThemeConfig as importedThemeConfig } from '../config/themeConfig.bundle.js';
import { bundledLogoConfig as importedLogoConfig } from '../config/logoConfig.bundle.js';
import { bundledTabsConfig as importedTabsConfig } from '../config/tabsConfig.bundle.js';

const bundledThemeConfig = importedThemeConfig;
const bundledLogoConfig = importedLogoConfig;
const bundledTabsConfig = importedTabsConfig;

/**
 * Fetch JSON config from public/UserData folder
 * Uses cache-busting to ensure fresh configs when needed
 * @param {string} filename - Name of the JSON file
 * @param {boolean} bustCache - Whether to add cache-busting query param
 * @returns {Promise<Object>} - Parsed JSON object
 */
const fetchConfig = async (filename, bustCache = false) => {
  try {
    let url = `/UserData/${filename}`;
    if (bustCache) {
      url += `?t=${Date.now()}`;
    }
    const response = await fetch(url, {
      // Request fresh copy when service worker is network-first anyway,
      // but this ensures browser doesn't use stale HTTP cache
      cache: bustCache ? 'no-cache' : 'default'
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${filename}:`, error);
    throw error;
  }
};

/**
 * Load theme configuration
 * 
 * Priority:
 * 1. Bundled config (compile-time, fastest, no network request)
 * 2. Runtime fetch from /UserData/ (if bundled not available)
 * 3. Fallback defaults (if both fail)
 */
export const loadUserThemeConfig = async () => {
  // First, try bundled config (compile-time)
  if (bundledThemeConfig && bundledThemeConfig.type === 'theme' && bundledThemeConfig.colors) {
    console.log('Loaded theme config from bundle (compile-time)');
    return {
      colors: bundledThemeConfig.colors,
      font: bundledThemeConfig.font || DEFAULT_FONT
    };
  }

  // Fall back to runtime fetch
  try {
    const themeConfig = await fetchConfig('wedding-theme-config.json');
    
    // Validate config structure
    if (themeConfig.type === 'theme' && themeConfig.colors) {
      console.log('Loaded theme config from UserData (runtime)');
      return {
        colors: themeConfig.colors,
        font: themeConfig.font || DEFAULT_FONT
      };
    }
    
    throw new Error('Invalid theme config format');
  } catch (error) {
    console.error('Failed to load theme config:', error);
    // Return fallback default if config is invalid
    return {
      colors: {
        primary: '#8EA58C',
        secondary: '#5C4033',
        accent: '#BFCFBB',
        neutral: '#D9C2A8',
        surface: '#ffffff'
      },
      font: DEFAULT_FONT
    };
  }
};

/**
 * Load logo configuration
 * 
 * Priority:
 * 1. Bundled config (compile-time, fastest)
 * 2. Runtime fetch from /UserData/
 * 3. Fallback defaults
 */
export const loadUserLogoConfig = async () => {
  // First, try bundled config (compile-time)
  if (bundledLogoConfig && bundledLogoConfig.type === 'logo' && bundledLogoConfig.logoFileName && bundledLogoConfig.weddingText) {
    console.log('Loaded logo config from bundle (compile-time)');
    const logoPath = `/UserData/${bundledLogoConfig.logoFileName}`;
    return {
      logo: logoPath,
      text: bundledLogoConfig.weddingText
    };
  }

  // Fall back to runtime fetch
  try {
    const logoConfig = await fetchConfig('wedding-logo-config.json');
    
    // Validate config structure
    if (logoConfig.type === 'logo' && logoConfig.logoFileName && logoConfig.weddingText) {
      console.log('Loaded logo config from UserData (runtime)');
      const logoPath = `/UserData/${logoConfig.logoFileName}`;
      return {
        logo: logoPath,
        text: logoConfig.weddingText
      };
    }
    
    throw new Error('Invalid logo config format');
  } catch (error) {
    console.error('Failed to load logo config:', error);
    // Return fallback default if config is invalid
    return {
      logo: '/UserData/Logo.svg',
      text: 'Wedding of Groom & Bride'
    };
  }
};

/**
 * Check if we're in demo mode
 * Demo mode shows the customize buttons, production mode hides them
 * Both modes use the same data loading logic (UserData → localStorage cache)
 */
export const isDemoMode = () => {
  return process.env.REACT_APP_DEMO_MODE === 'true';
};

/**
 * Force refresh all configs from server
 * Clears localStorage and fetches fresh copies
 * Use this when you need to manually invalidate all cached configs
 */
export const forceRefreshAllConfigs = async () => {
  console.log('🔄 Force refreshing all configs from server...');
  
  // Clear localStorage caches
  localStorage.removeItem('wedding_theme_config');
  localStorage.removeItem('wedding_logo_config');
  localStorage.removeItem('wedding_tabs_config');
  
  // Fetch fresh copies with cache busting
  try {
    const [theme, logo, tabs] = await Promise.all([
      fetchConfig('wedding-theme-config.json', true),
      fetchConfig('wedding-logo-config.json', true),
      fetchConfig('wedding-tabs-config.json', true)
    ]);
    
    console.log('✓ All configs refreshed from server');
    return { theme, logo, tabs };
  } catch (error) {
    console.error('Failed to refresh configs:', error);
    throw error;
  }
};

/**
 * Load tabs configuration
 * 
 * Priority:
 * 1. Bundled config (compile-time, fastest)
 * 2. Runtime fetch from /UserData/
 * 3. Fallback defaults
 */
export const loadUserTabsConfig = async () => {
  // First, try bundled config (compile-time)
  if (bundledTabsConfig && bundledTabsConfig.type === 'tabs') {
    console.log('Loaded tabs config from bundle (compile-time)');
    const floorplanPath = bundledTabsConfig.floorplanFileName 
      ? `/UserData/${bundledTabsConfig.floorplanFileName}`
      : '/UserData/floorplan.png';
    const menuPath = bundledTabsConfig.menuFileName 
      ? `/UserData/${bundledTabsConfig.menuFileName}`
      : null;
    return {
      floorplan: floorplanPath,
      menu: menuPath,
      showVenueTab: bundledTabsConfig.showVenueTab !== false,
      showMediaFilter: bundledTabsConfig.showMediaFilter !== false,
      showGuestbookTab: bundledTabsConfig.showGuestbookTab !== false,
      showQAndATab: bundledTabsConfig.showQAndATab !== false
    };
  }

  // Fall back to runtime fetch
  try {
    const tabsConfig = await fetchConfig('wedding-tabs-config.json');
    
    // Validate config structure
    if (tabsConfig.type === 'tabs') {
      console.log('Loaded tabs config from UserData (runtime)');
      const floorplanPath = tabsConfig.floorplanFileName 
        ? `/UserData/${tabsConfig.floorplanFileName}`
        : '/UserData/floorplan.png';
      const menuPath = tabsConfig.menuFileName 
        ? `/UserData/${tabsConfig.menuFileName}`
        : null;
      return {
        floorplan: floorplanPath,
        menu: menuPath,
        showVenueTab: tabsConfig.showVenueTab !== false,
        showMediaFilter: tabsConfig.showMediaFilter !== false,
        showGuestbookTab: tabsConfig.showGuestbookTab !== false,
        showQAndATab: tabsConfig.showQAndATab !== false
      };
    }
    
    throw new Error('Invalid tabs config format');
  } catch (error) {
    console.error('Failed to load tabs config:', error);
    // Return fallback default if config is invalid
    return {
      floorplan: '/UserData/floorplan.png',
      menu: null,
      showVenueTab: true,
      showMediaFilter: true,
      showGuestbookTab: true,
      showQAndATab: true
    };
  }
};
